import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import OfflineBanner from '../components/OfflineBanner';
import api from '../services/api';
import useDialogSizePersistence from '../hooks/useDialogSizePersistence';
import { 
  Calendar, 
  MapPin, 
  Users, 
  LogOut,
  Menu,
  X,
  Briefcase,
  Home,
  Building2,
  CalendarDays,
  Settings,
  BarChart3,
  User,
  Trash2,
  UserCog,
  Activity,
  Loader2,
  Phone,
  Upload,
  Globe,
  ChevronDown,
  Check,
  Mail,
  Shield,
  FileText,
  Trophy,
  Package,
  Truck,
  Warehouse,
  ChevronRight,
  Smartphone,
  WifiOff
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const navItems = [
  { path: '/home', label: 'Home', icon: Home },
  { path: '/schedule', label: 'Schedule', icon: Calendar },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/reports-repository', label: 'Audit Reports', icon: FileText },
  { path: '/customers', label: 'Customers', icon: Building2, permission: 'canViewSites' },
];

// Wave 5A P1 — Stocktaking is now a direct link to the Stocktaking landing
// page (no dropdown). All sub-items live as tiles on that landing page.

// Secondary nav items shown in the "More" dropdown - for Admin/Scheduler
const secondaryNavItems = [
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/services', label: 'Services', icon: Briefcase, permission: 'isAdmin' },
  { path: '/users', label: 'Users', icon: UserCog, permission: 'isAdmin' },
  { path: '/holidays', label: 'Holidays', icon: Calendar, permission: 'canManageSites' },
  { path: '/notifications/logs', label: 'Notifications', icon: Mail, permission: 'canManageSites' },
  { path: '/activity', label: 'Activity', icon: Activity, permission: 'isAdmin' },
  { path: '/maintenance', label: 'Maintenance', icon: Shield, permission: 'isAdmin' },
  { path: '/settings', label: 'Settings', icon: Settings, permission: 'isAdmin' },
];

// Auditor-specific nav - direct items only (no dropdown)
const auditorNavItems = [
  { path: '/home', label: 'Home', icon: Home },
  { path: '/schedule', label: 'Schedule', icon: Calendar },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/customers', label: 'Customers', icon: Building2 },
  { path: '/reports-repository', label: 'Audit Reports', icon: FileText },  // Re-enabled for auditors
];

export default function Layout({ children }) {
  const { user, logout, logoutAndClearDevice, isOfflineLogin, canManageSites, canViewSites, canManageAuditors, isAdmin, isAuditor, isScheduler, isSeniorScheduler, hasAnyRole, refreshUser, canApproveBatches } = useAuth();
  const { region, currencySymbol, setRegion } = useSettings();
  const { isImpersonating } = useImpersonation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [regionSwitching, setRegionSwitching] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // iter463 — Persist resizable dialog sizes per-user across sessions.
  useDialogSizePersistence(user?.id);

  // Track online/offline state
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);
  
  // Phone placeholder based on region
  const phonePlaceholder = region === 'UAE' ? '+971 50 123 4567' : '+44 7700 900000';
  
  // Helper to get backend URL (same logic as api.js)
  const getBackendUrl = () => {
    const currentHost = window.location.hostname;
    if (currentHost.includes('.emergent.host') || currentHost.includes('.preview.')) {
      return window.location.origin;
    }
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    }
    return window.location.origin;
  };

  // Helper to get profile photo URL (handles both R2 and legacy local storage)
  const getProfilePhotoUrl = (photoUrl) => {
    if (!photoUrl) return undefined;
    
    const backendUrl = getBackendUrl();
    let resultUrl;
    
    // R2 storage format: profile_photos/{org_id}/{filename}
    // Backend endpoint: /api/users/profile-photo/{org_id}/{filename}
    if (photoUrl.startsWith('profile_photos/')) {
      // Extract org_id and filename from the path
      const parts = photoUrl.split('/');
      if (parts.length >= 3) {
        const orgId = parts[1];
        const filename = parts[2];
        resultUrl = `${backendUrl}/api/users/profile-photo/${orgId}/${filename}`;
      }
    }
    // Legacy local storage format: /api/uploads/profile_photos/{filename}
    else if (photoUrl.startsWith('/api/')) {
      resultUrl = `${backendUrl}${photoUrl}`;
    }
    // Already a full URL
    else {
      resultUrl = photoUrl;
    }
    
    console.log('[PHOTO URL]', { input: photoUrl, output: resultUrl, backendUrl });
    return resultUrl;
  };
  
  // Profile modal state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    profile_photo_url: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLogoutClearDevice = async () => {
    await logoutAndClearDevice();
    navigate('/login');
  };

  const openProfileDialog = () => {
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone_number: user?.phone_number || '',
      profile_photo_url: user?.profile_photo_url || ''
    });
    setPhotoPreview(null);
    setProfileDialogOpen(true);
  };

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a JPG, PNG, GIF, or WebP image',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 5MB',
        variant: 'destructive'
      });
      return;
    }

    // Show local preview
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload photo
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/users/me/profile-photo', formData);
      
      const newPhotoUrl = response.data.profile_photo_url;
      console.log('[PHOTO UPLOAD] Server returned URL:', newPhotoUrl, 'Storage:', response.data.storage_type);
      
      setProfileForm(prev => ({ ...prev, profile_photo_url: newPhotoUrl }));
      
      // Refresh user from server to update context and localStorage with fresh data
      const freshUser = await refreshUser();
      console.log('[PHOTO UPLOAD] After refreshUser, user.profile_photo_url:', freshUser?.profile_photo_url);
      
      toast({ 
        title: 'Success', 
        description: `Photo uploaded (${response.data.storage_type === 'r2' ? 'cloud storage' : 'local'})` 
      });
    } catch (err) {
      setPhotoPreview(null);
      toast({
        title: 'Upload failed',
        description: getApiError(err, 'Failed to upload photo'),
        variant: 'destructive'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await api.delete('/users/me/profile-photo');
      setProfileForm(prev => ({ ...prev, profile_photo_url: '' }));
      setPhotoPreview(null);
      
      // Refresh user from server to update context and localStorage
      await refreshUser();
      
      toast({ title: 'Success', description: 'Photo removed' });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to remove photo',
        variant: 'destructive'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      // Don't send profile_photo_url in the update - it's handled separately
      const { profile_photo_url, ...updateData } = profileForm;
      await api.put(`/users/${user.id}`, updateData);
      toast({ title: 'Success', description: 'Profile updated' });
      setProfileDialogOpen(false);
      // Update user in local storage
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ 
        ...storedUser, 
        ...updateData,
        profile_photo_url: profileForm.profile_photo_url 
      }));
      // Force reload to update context
      window.location.reload();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to update profile'),
        variant: 'destructive'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const permissions = { canManageSites, canViewSites, canManageAuditors, isAdmin, canApproveBatches };
  
  // Auditor-only: has auditor role but not admin, senior_scheduler, or scheduler
  const isAuditorOnly = isAuditor && !isAdmin && !hasAnyRole(['scheduler', 'senior_scheduler']);
  
  // Get display roles (from roles array or legacy role)
  const displayRoles = user?.roles?.length > 0 
    ? user.roles.map(r => {
        if (r === 'senior_scheduler') return 'Senior Scheduler';
        return r.charAt(0).toUpperCase() + r.slice(1);
      }).join(', ')
    : user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '';

  // For auditors, use simplified nav (no dropdown)
  // For admin/scheduler, use full nav with dropdown
  // When offline, collapse to only offline-capable destinations
  const offlineNavItems = [
    { path: '/count', label: 'AbbeyZone Count', icon: Smartphone },
    { path: '/stocktaking/offline', label: 'Offline Sessions', icon: WifiOff },
  ];

  // Offline-capable path prefixes — only these routes function without a server connection
  const OFFLINE_CAPABLE_PREFIXES = ['/count', '/stocktaking/offline', '/stocktaking/sessions/', '/change-password'];

  // Redirect to offline hub if user navigates to a non-offline route while offline
  useEffect(() => {
    if (!isOnline) {
      const isOfflineCapable = OFFLINE_CAPABLE_PREFIXES.some(prefix => location.pathname.startsWith(prefix));
      if (!isOfflineCapable) {
        navigate('/stocktaking/offline', { replace: true });
      }
    }
  }, [isOnline, location.pathname, navigate]);

  const visibleNavItems = !isOnline
    ? offlineNavItems
    : isAuditorOnly 
      ? auditorNavItems  // Simplified direct nav for auditors
      : navItems.filter(item => {
          if (!item.permission) return true;
          return permissions[item.permission];
        });

  // Only show dropdown for non-auditor users who are online
  const visibleSecondaryNavItems = (!isOnline || isAuditorOnly) 
    ? []  // No dropdown when offline or for auditors
    : secondaryNavItems.filter(item => {
        if (!item.permission) return true;
        return permissions[item.permission];
      });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="app-layout">
      <Toaster />
      <SonnerToaster position="bottom-right" richColors />
      {/* Header */}
      <header className={`bg-white border-b border-slate-200 sticky z-50 ${isImpersonating ? 'top-10' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - 40% larger than original (56px to 78px) for better visibility */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to="/home" className="flex items-center gap-3" data-testid="nav-logo">
                <img 
                  src="/logo.png" 
                  alt="Abbey 5 Star Ltd" 
                  className="w-auto object-contain"
                  style={{ height: '78px' }}
                />
                <span className="font-semibold text-lg hidden lg:block text-slate-800 whitespace-nowrap">
                  Abbey 5 Star Ltd
                </span>
              </Link>
            </div>
            
            {/* Region Selector - Interactive for Admins, Badge for others */}
            {isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={regionSwitching}
                    className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium flex-shrink-0 ${
                      region === 'UAE' 
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' 
                        : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                    }`}
                    data-testid="region-selector"
                  >
                    {regionSwitching ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <Globe className="h-3 w-3" />
                        <span>{region === 'UAE' ? '🇦🇪' : '🇬🇧'}</span>
                        <span>{region}</span>
                        <span className="text-muted-foreground">({currencySymbol})</span>
                        <ChevronDown className="h-3 w-3 ml-0.5" />
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Globe className="h-3 w-3" />
                    Switch Region
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={async () => {
                      if (region === 'UK') return;
                      setRegionSwitching(true);
                      try {
                        await setRegion('UK');
                        toast({ 
                          title: 'Region switched', 
                          description: 'Now viewing UK data' 
                        });
                        // iter448 — reload so every open view refetches with
                        // the new X-Region header (ring-fence takes effect).
                        setTimeout(() => window.location.reload(), 250);
                      } catch (err) {
                        toast({ 
                          title: 'Error', 
                          description: 'Failed to switch region',
                          variant: 'destructive'
                        });
                      } finally {
                        setRegionSwitching(false);
                      }
                    }}
                    className="cursor-pointer"
                    data-testid="region-option-uk"
                  >
                    <span className="mr-2">🇬🇧</span>
                    <span className="flex-1">United Kingdom (£)</span>
                    {region === 'UK' && <Check className="h-4 w-4 text-green-600" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={async () => {
                      if (region === 'UAE') return;
                      setRegionSwitching(true);
                      try {
                        await setRegion('UAE');
                        toast({ 
                          title: 'Region switched', 
                          description: 'Now viewing UAE data' 
                        });
                        // iter448 — see comment above.
                        setTimeout(() => window.location.reload(), 250);
                      } catch (err) {
                        toast({ 
                          title: 'Error', 
                          description: 'Failed to switch region',
                          variant: 'destructive'
                        });
                      } finally {
                        setRegionSwitching(false);
                      }
                    }}
                    className="cursor-pointer"
                    data-testid="region-option-uae"
                  >
                    <span className="mr-2">🇦🇪</span>
                    <span className="flex-1">UAE (AED)</span>
                    {region === 'UAE' && <Check className="h-4 w-4 text-green-600" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Badge 
                variant="outline" 
                className={`hidden md:flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium flex-shrink-0 ${
                  region === 'UAE' 
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                    : 'bg-blue-50 border-blue-200 text-blue-700'
                }`}
                data-testid="region-badge"
              >
                <span>{region === 'UAE' ? '🇦🇪' : '🇬🇧'}</span>
                <span>{region}</span>
                <span className="text-muted-foreground">({currencySymbol})</span>
              </Badge>
            )}

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/schedule' && location.pathname.startsWith(item.path));
                return (
                  <Link key={item.path} to={item.path}>
                    <Button 
                      variant={isActive ? 'secondary' : 'ghost'} 
                      size="sm"
                      className="gap-2"
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
              

              {/* Wave 5A P1 — Stocktaking is a direct link to the Stocktaking
                  landing page (no dropdown). All sub-items are tiles there. */}
              {canViewSites && (
                <Link to="/stocktaking/dashboard">
                  <Button
                    variant={location.pathname.startsWith('/stocktaking') || location.pathname === '/count' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="gap-2"
                    data-testid="nav-stocktaking"
                  >
                    <Warehouse className="h-4 w-4" />
                    Stocktaking
                  </Button>
                </Link>
              )}

              {visibleSecondaryNavItems.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="nav-more-btn" className="gap-1">
                      <Menu className="h-4 w-4" />
                      More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {visibleSecondaryNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path || 
                        (item.path !== '/schedule' && location.pathname.startsWith(item.path));
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link 
                            to={item.path} 
                            className={`flex items-center ${isActive ? 'bg-slate-100' : ''}`}
                            data-testid={`nav-${item.label.toLowerCase()}`}
                          >
                            <Icon className="h-4 w-4 mr-2" />
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>

            {/* User Info & Logout */}
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    {/* Profile photo in header */}
                    <Avatar className="h-8 w-8 border border-slate-200">
                      <AvatarImage src={getProfilePhotoUrl(user?.profile_photo_url)} />
                      <AvatarFallback className="text-xs bg-slate-100">
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium" data-testid="user-name">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize" data-testid="user-role">
                        {displayRoles}{isOfflineLogin ? ' (Offline)' : ''}
                      </p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={openProfileDialog} data-testid="my-profile-btn">
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogoutClearDevice} 
                    className="text-destructive focus:text-destructive"
                    data-testid="logout-clear-device-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Log Out &amp; Clear Device
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <nav className="px-4 py-2 space-y-1">
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button 
                      variant={isActive ? 'secondary' : 'ghost'} 
                      className="w-full justify-start gap-2"
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
              {visibleSecondaryNavItems.length > 0 && (
                <div className="border-t border-slate-200 pt-2 mt-2">
                  {visibleSecondaryNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                      <Link 
                        key={item.path} 
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button 
                          variant={isActive ? 'secondary' : 'ghost'} 
                          className="w-full justify-start gap-2"
                        >
                          <Icon className="h-4 w-4" />
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <OfflineBanner />
      {/* iter363c — The Schedule Batch view is a dense weekly grid
          with one column per auditor; constraining it to max-w-7xl
          wastes ~30 % of horizontal space on wider monitors. Detect
          the route and let it stretch full width. */}
      {location.pathname === '/schedule/batch' ? (
        <main className="flex-1 w-full px-2 sm:px-3 lg:px-4 py-4" data-testid="main-batch-fullwidth">
          {children}
        </main>
      ) : location.pathname.startsWith('/reports/revenue-budget') ? (
        /* iter-issue9 — Revenue Budget is a wide data grid; remove the
           1280px cap so the table uses all available viewport width.
           Same horizontal padding as the default shell; no mx-auto. */
        <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-4" data-testid="main-budget-fullwidth">
          {children}
        </main>
      ) : (
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </main>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Abbey 5 Star Ltd" className="h-8 w-auto opacity-70" />
              <span>© {new Date().getFullYear()} Abbey 5 Star Ltd. All rights reserved.</span>
            </div>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
              <Link to="/trademark" className="hover:text-foreground">Trademark</Link>
              <Link to="/contact" className="hover:text-foreground">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* My Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              My Profile
            </DialogTitle>
            <DialogDescription>
              Update your profile information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveProfile}>
            <div className="space-y-4 py-4">
              {/* Avatar Preview with Upload */}
              <div className="flex flex-col items-center gap-3">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={photoPreview || getProfilePhotoUrl(profileForm.profile_photo_url)} />
                  <AvatarFallback className="text-2xl bg-slate-100">
                    {profileForm.first_name?.charAt(0)}{profileForm.last_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handlePhotoSelect}
                      className="hidden"
                      disabled={uploadingPhoto}
                      data-testid="profile-photo-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      disabled={uploadingPhoto}
                      asChild
                    >
                      <span>
                        {uploadingPhoto ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Upload className="h-4 w-4 mr-1" />
                        )}
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </span>
                    </Button>
                  </label>
                  {(profileForm.profile_photo_url || photoPreview) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemovePhoto}
                      disabled={uploadingPhoto}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  JPG, PNG, GIF or WebP. Max 5MB.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={profileForm.first_name}
                    onChange={(e) => handleProfileChange('first_name', e.target.value)}
                    placeholder="First name"
                    data-testid="profile-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={profileForm.last_name}
                    onChange={(e) => handleProfileChange('last_name', e.target.value)}
                    placeholder="Last name"
                    data-testid="profile-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone_number"
                  value={profileForm.phone_number || ''}
                  onChange={(e) => handleProfileChange('phone_number', e.target.value)}
                  placeholder={phonePlaceholder}
                  data-testid="profile-phone"
                />
              </div>

              {/* Display-only info */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Email</span>
                  <span>{user?.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Role(s)</span>
                  <span className="capitalize">{displayRoles}</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProfileDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn">
                {savingProfile && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
